import { CustomElement, resolve, IPlatform } from 'aurelia';
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  type ChartDataset,
} from 'chart.js';
import { DailyReleasesAdminService, type DailyReleaseSeries } from '../../services';
import template from './daily-releases-admin-panel.html';
import './daily-releases-admin-panel.css';

// Register only the Chart.js pieces we use (tree-shakeable build) once per module load.
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// The 24 local-hour labels shown along the x-axis.
const HOUR_LABELS: string[] = Array.from({ length: 24 }, (_, h) => {
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? 'am' : 'pm';
  return `${hour12}${suffix}`;
});

// A colorblind-friendly 20-color palette (Tableau 20) assigned per source so datasets stay
// visually distinct. For the rare case of more sources than palette entries, colorForIndex falls
// back to evenly-spaced hues via the golden angle so colors still never repeat.
const PALETTE: string[] = [
  '#4e79a7', '#a0cbe8', '#f28e2b', '#ffbe7d', '#59a14f',
  '#8cd17d', '#b6992d', '#f1ce63', '#499894', '#86bcb6',
  '#e15759', '#ff9d9a', '#79706e', '#bab0ac', '#d37295',
  '#fabfd2', '#b07aa1', '#d4a6c8', '#9d7660', '#d7b5a6',
];

// Returns a stable, distinct color for the dataset at position i. Uses the fixed palette first,
// then generates additional hues with the golden-angle increment (137.508°) so large source
// counts still get non-repeating colors.
function colorForIndex(i: number): string {
  if (i < PALETTE.length) return PALETTE[i];
  const hue = (i * 137.508) % 360;
  return `hsl(${hue.toFixed(1)}, 65%, 55%)`;
}

// Admin-only modal charting the hours of the day (browser-local time) at which each source tends
// to first publish new releases for the selected day. Opened from the admin menu; all data/day
// navigation state lives in DailyReleasesAdminService. This component owns only the Chart.js
// instance and mirrors the service's per-source hourly series into it, re-rendering whenever the
// selected day changes.
export class DailyReleasesAdminPanelElement {
  readonly report: DailyReleasesAdminService = resolve(DailyReleasesAdminService);
  private readonly platform: IPlatform = resolve(IPlatform);

  canvas!: HTMLCanvasElement;
  slide!: HTMLElement;
  private chart: Chart | null = null;

  // Serialized snapshot of the last-rendered state so the observer only re-renders on real change.
  private lastRenderKey: string = '';

  attached(): void {
    // Poll the service's render key on each frame while attached; cheap string compare avoids
    // rebuilding the chart unless the day's data actually changed. Aurelia's binding engine does
    // not observe the Chart canvas, so we drive updates from the service state directly.
    this.tick = this.tick.bind(this);
    this.platform.requestAnimationFrame(this.tick);
  }

  detaching(): void {
    this.rafHandle = 0;
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private rafHandle: number = 0;

  private tick(): void {
    if (!this.report.panelOpen) {
      // Keep polling cheaply while closed so re-opening resumes without re-attach.
      this.rafHandle = this.platform.requestAnimationFrame(this.tick);
      return;
    }

    const key = this.renderKey();
    if (key !== this.lastRenderKey) {
      // While a new day is loading, `series` still holds the previous day's data. Rendering it here
      // would replay the animation with stale data and then animate again once the fresh data
      // arrives (a visible double refresh). Skip rendering until loading completes so the chart
      // animates exactly once, for the new day's data.
      if (this.report.loading) {
        this.rafHandle = this.platform.requestAnimationFrame(this.tick);
        return;
      }

      this.lastRenderKey = key;
      this.render(this.report.series);
      this.replaySlide();
    }

    this.rafHandle = this.platform.requestAnimationFrame(this.tick);
  }

  // A cheap change signal: the selected day plus each source and its per-hour counts.
  private renderKey(): string {
    if (this.report.loading) return `loading:${this.report.selectedDay.getTime()}`;
    return (
      this.report.selectedDay.getTime() +
      '|' +
      this.report.series.map((s) => `${s.source}:${s.countsByHour.join(',')}`).join(';')
    );
  }

  private render(series: DailyReleaseSeries[]): void {
    if (!this.canvas) return;

    const datasets: ChartDataset<'bar'>[] = series.map((s, i) => ({
      label: s.source,
      data: s.countsByHour,
      backgroundColor: colorForIndex(i),
      stack: 'releases',
    }));

    if (!this.chart) {
      this.chart = new Chart(this.canvas, {
        type: 'bar',
        data: { labels: HOUR_LABELS, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 300 },
          plugins: {
            legend: {
              position: 'bottom',
              labels: { usePointStyle: true, pointStyle: 'rect' },
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              // Only list sources that actually have releases in the hovered hour; hide the many
              // zero-filled sources that exist purely to keep the legend/colors stable.
              filter: (item) => (item.parsed.y ?? 0) > 0,
            },
          },
          scales: {
            x: { stacked: true, title: { display: true, text: 'Hour of day (local time)' } },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { precision: 0 },
              title: { display: true, text: 'New releases' },
            },
          },
        },
      });
      return;
    }

    this.chart.data.datasets = datasets;
    this.chart.update();
  }

  // Restarts the horizontal slide animation on each day change. Toggling the class alone does not
  // replay a CSS animation when the value repeats (e.g. "next" twice), so we remove the classes,
  // force a reflow, then re-add the direction class.
  private replaySlide(): void {
    if (!this.slide || !this.report.lastDirection) return;
    const cls = `daily-releases-admin__chart-slide--${this.report.lastDirection}`;
    this.slide.classList.remove(
      'daily-releases-admin__chart-slide--prev',
      'daily-releases-admin__chart-slide--next',
    );
    // Force reflow so the browser registers the class removal before re-adding.
    void this.slide.offsetWidth;
    this.slide.classList.add(cls);
  }
}

export const DailyReleasesAdminPanel = CustomElement.define(
  {
    name: 'daily-releases-admin-panel',
    template,
  },
  DailyReleasesAdminPanelElement,
);
